/**
 * Environment Configuration
 * Centralized access to environment variables
 */
import { debugLog } from '../utils/debug';

export interface AppConfig {
    apiBaseUrl: string;
    appName: string;
    appVersion: string;
    debugMode: boolean;
    logLevel: 'debug' | 'info' | 'warn' | 'error';
    gzipThresholdBytes: number;
    /** Cloudflare Turnstile site key (public); empty disables the widget. */
    turnstileSiteKey: string;
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, fallback: string = ''): string {
    return import.meta.env[key] || fallback;
}

/**
 * Get boolean environment variable
 */
function getBooleanEnvVar(key: string, fallback: boolean = false): boolean {
    const value = getEnvVar(key);
    if (value === '') return fallback;
    return value.toLowerCase() === 'true';
}

/**
 * Get numeric environment variable
 */
function getNumericEnvVar(key: string, fallback: number): number {
    const value = getEnvVar(key);
    if (value === '') return fallback;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
}

/**
 * Application configuration
 */
export const config: AppConfig = {
    // API Configuration
    apiBaseUrl: getEnvVar('VITE_API_BASE_URL', import.meta.env.DEV ? 'https://dev.localhost:7201/api' : ''),

    // Application Configuration
    appName: getEnvVar('VITE_APP_NAME', 'Calculist'),
    appVersion: getEnvVar('VITE_APP_VERSION', '1.0.0'),

    // Development Configuration
    debugMode: getBooleanEnvVar('VITE_DEBUG_MODE', true),
    logLevel: (getEnvVar('VITE_LOG_LEVEL', 'debug') as AppConfig['logLevel']) || 'debug',

    // Compression Configuration
    // Gzip threshold in bytes. Set to 0 to gzip everything, set to a very large number (e.g., 2000000000) to disable gzipping
    gzipThresholdBytes: getNumericEnvVar('VITE_GZIP_THRESHOLD_BYTES', 10 * 1024), // Default: 10 KB

    turnstileSiteKey: getEnvVar('VITE_TURNSTILE_SITE_KEY', ''),
};

/**
 * `VITE_API_BASE_URL` is typically `https://host/.../api`. API controllers use `api/v1/...` (same as BaseApiService `/v1/...`).
 * HealthController: `api/v1/health/version`, `api/v1/health/detailed`. MapHealthChecks in Program.cs: `/api/health`.
 */
export function getHealthVersionUrl(): string {
    const base = config.apiBaseUrl;
    return base.endsWith('/api') ? `${base}/v1/health/version` : `${base}/api/v1/health/version`;
}

/** ASP.NET Core MapHealthChecks endpoint (not HealthController). */
export function getMapHealthChecksUrl(): string {
    const base = config.apiBaseUrl;
    return base.endsWith('/api') ? `${base}/health` : `${base}/api/health`;
}

export function getHealthDetailedUrl(): string {
    const base = config.apiBaseUrl;
    return base.endsWith('/api') ? `${base}/v1/health/detailed` : `${base}/api/v1/health/detailed`;
}

/**
 * Environment detection
 */
export const isDevelopment = import.meta.env.DEV;
export const isProduction = import.meta.env.PROD;

/**
 * Get the actual runtime environment based on API URL or explicit environment variable
 */
function getRuntimeEnvironment(): string {
    // Check for explicit environment variable first
    const explicitEnv = getEnvVar('VITE_RUNTIME_ENVIRONMENT');
    if (explicitEnv) {
        return explicitEnv;
    }

    // Determine environment based on API base URL
    const apiUrl = getEnvVar('VITE_API_BASE_URL', import.meta.env.DEV ? 'https://dev.localhost:7201/api' : '');

    if (apiUrl.includes('dev.localhost:7201') || apiUrl.includes('localhost:7201')) {
        return 'dev-local';
    } else if (apiUrl.includes('cert-api.calculist.app')) {
        return 'cert';
    } else if (apiUrl.includes('test-api.calculist.app')) {
        return 'test';
    } else if (apiUrl.includes('api.calculist.app')) {
        return 'prod';
    }

    // Fallback to Vite mode if we can't determine from URL
    return import.meta.env.MODE;
}

export const runtimeEnvironment = getRuntimeEnvironment();

/**
 * Log configuration in development
 */
if (isDevelopment && config.debugMode) {
    // debugLog('🔧 Environment Configuration:', {
    //     runtimeEnvironment,
    //     isDevelopment,
    //     isProduction,
    //     config,
    // });
}
